/**
 * Sample signed-in devices for the Manage devices screen.
 * Replace this module when the devices API is ready.
 * Limit card, plan card, device list, and primary note each have a skeleton.
 */

export type DeviceKind = 'phone' | 'laptop';
export type DevicePresence = 'active' | 'idle';
export type DeviceSort = 'recent' | 'name' | 'status';

export interface AccountDevice {
  id: string;
  name: string;
  detail: string;
  kind: DeviceKind;
  presence: DevicePresence;
  /** Short status beside the dot. */
  presenceLabel: string;
  /** Absolute last-activity stamp, already formatted. */
  lastActiveLabel: string;
  /** Relative line under the stamp. Empty when the device is active now. */
  agoLabel: string;
  lastActiveAt: number;
  current: boolean;
  primary: boolean;
}

export const PLAN_DEVICE_LIMIT = 3;

export const FULL_LIMIT_HINT =
  'To use MockLoc on a new device, remove one of your existing devices first.';

export const PRIMARY_NOTE =
  'Your primary device is the main device for your account. If you remove this device, another active device will be set as primary automatically.';

export const SAMPLE_DEVICES: AccountDevice[] = [
  {
    id: 'samsung-now',
    name: 'Samsung SM-E426B',
    detail: 'Android 13',
    kind: 'phone',
    presence: 'active',
    presenceLabel: 'Active now',
    lastActiveLabel: '20 Oct 2026, 07:30',
    agoLabel: '',
    lastActiveAt: Date.UTC(2026, 9, 20, 7, 30),
    current: true,
    primary: false,
  },
  {
    id: 'windows-chrome',
    name: 'Windows PC · Chrome',
    detail: 'Windows 11 · Chrome 118',
    kind: 'laptop',
    presence: 'idle',
    presenceLabel: 'Last active',
    lastActiveLabel: '18 Oct 2026, 10:15',
    agoLabel: '2 days ago',
    lastActiveAt: Date.UTC(2026, 9, 18, 10, 15),
    current: false,
    primary: false,
  },
  {
    id: 'samsung-primary',
    name: 'Samsung SM-E426B',
    detail: 'Android 13',
    kind: 'phone',
    presence: 'idle',
    presenceLabel: 'Last active',
    lastActiveLabel: '15 Oct 2026, 09:20',
    agoLabel: '5 days ago',
    lastActiveAt: Date.UTC(2026, 9, 15, 9, 20),
    current: false,
    primary: true,
  },
];

export function cloneDevices(devices: AccountDevice[]): AccountDevice[] {
  return devices.map((device) => ({ ...device }));
}

export function safeDeviceLimit(limit: number | undefined): number {
  if (limit == null || !Number.isFinite(limit) || limit < 1) return PLAN_DEVICE_LIMIT;
  return Math.floor(limit);
}

export function usageText(count: number, limit: number): string {
  return `${count} of ${limit} devices in use`;
}

export function usagePercent(count: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((count / limit) * 100)));
}

export function usageHint(count: number, limit: number): string {
  const room = limit - count;
  if (room <= 0) return FULL_LIMIT_HINT;
  if (room === 1) return 'You can sign in on 1 more device.';
  return `You can sign in on ${room} more devices.`;
}

export function planText(limit: number): string {
  const noun = limit === 1 ? 'device' : 'devices';
  return `You can use MockLoc on up to ${limit} ${noun}. This limit is set by your current plan.`;
}

export function sortDevices(devices: AccountDevice[], sort: DeviceSort): AccountDevice[] {
  const next = devices.map((device) => ({ ...device }));
  if (sort === 'name') {
    next.sort(
      (a, b) => a.name.localeCompare(b.name) || b.lastActiveAt - a.lastActiveAt || a.id.localeCompare(b.id)
    );
    return next;
  }
  if (sort === 'status') {
    next.sort((a, b) => {
      const rank = (device: AccountDevice) => (device.presence === 'active' ? 0 : 1);
      return rank(a) - rank(b) || b.lastActiveAt - a.lastActiveAt || a.id.localeCompare(b.id);
    });
    return next;
  }
  next.sort((a, b) => b.lastActiveAt - a.lastActiveAt || a.id.localeCompare(b.id));
  return next;
}

export function setPrimaryDevice(devices: AccountDevice[], id: string): AccountDevice[] {
  if (!devices.some((device) => device.id === id)) return devices.map((device) => ({ ...device }));
  return devices.map((device) => ({ ...device, primary: device.id === id }));
}

export function removeDevice(devices: AccountDevice[], id: string): AccountDevice[] {
  const removed = devices.find((device) => device.id === id);
  const next = devices.filter((device) => device.id !== id).map((device) => ({ ...device }));
  if (!removed || next.length === 0 || !removed.primary || next.some((device) => device.primary)) {
    return next;
  }
  const successor = [...next].sort((a, b) => b.lastActiveAt - a.lastActiveAt)[0];
  return next.map((device) => ({ ...device, primary: device.id === successor.id }));
}

export function removeMessage(device: AccountDevice, remaining: number): string {
  const parts = [`${device.name} will be signed out of this MockLoc account.`];
  if (device.current) parts.push('This is the device marked as in use now.');
  if (device.primary && remaining > 0) {
    parts.push('Another device will be set as primary automatically.');
  }
  return parts.join(' ');
}

export function isDeviceSort(value: string): value is DeviceSort {
  return value === 'recent' || value === 'name' || value === 'status';
}
